import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router';
import classnames from 'classnames';

import Widget from '../../../../../components/Widget/Widget';
import ConnectHero from '../../heroSection';
import ConnectTabs from '../../connectTabs';
import ConnectAboutSection from '../../aboutSection';
import ConnectIntegrationsSection from '../../integrationsSection';
import ConnectFaqSection from '../../faqSection';
import PricingSection from '../../../../../components/Billing/PricingSection';
import useConnectScrollSpy from '../../useConnectScrollSpy';
import useCheckoutState from '../../../../../components/CheckoutDrawer/useCheckoutState';
import CheckoutDrawer from '../../../../../components/CheckoutDrawer';
import { useStyles } from '../../styles';
import useUpgradeEcalPlanDialog from '../../../../hooks/useUpgradeEcalPlanDialog';
import usePlansBrowseViewState from './usePlansBrowseViewState';

const PRICING_HEADER = {
    title: 'Simple workspace pricing',
    subtitle: 'Three plans. Unlimited integrations. Set monthly fee.',
};

function PlansBrowseView () {
    const classes = useStyles();
    const history = useHistory();
    const { plans, loading, currency } = usePlansBrowseViewState();

    const aboutRef = useRef(null);
    const integrationsRef = useRef(null);
    const pricingRef = useRef(null);
    const faqRef = useRef(null);
    const refsById = { about: aboutRef, integrations: integrationsRef, pricing: pricingRef, faq: faqRef };

    const active = useConnectScrollSpy([
        { id: 'about', ref: aboutRef },
        { id: 'integrations', ref: integrationsRef },
        { id: 'pricing', ref: pricingRef },
        { id: 'faq', ref: faqRef },
    ]);

    const { checkoutOpen, selectedPlan, handleChoosePlan, closeCheckout } = useCheckoutState();

    const { isFreeTrialPlan, openUpgradeDialog, renderUpgradeDialog } = useUpgradeEcalPlanDialog();

    function gatedHandleChoosePlan (plan) {
        if (isFreeTrialPlan) {
            openUpgradeDialog();
            return;
        }
        handleChoosePlan(plan);
    }

    function scrollToSection (id) {
        const ref = refsById[id];
        if (!ref || !ref.current) {
            return;
        }
        // Offset for the fixed app header (~64px) + the sticky tab bar (~48px) so
        // the section heading lands just below them instead of being scrolled past.
        const OFFSET = 120;
        const top = ref.current.getBoundingClientRect().top + window.pageYOffset - OFFSET;
        window.scrollTo({ top, behavior: 'smooth' });
    }

    return (
        <div>
            <div className={classes.widgetContainer}>
                <Widget
                    className={classes.widgetContainer}
                    buttonFlag={false}
                    title='Connect'
                    inheritHeight
                    disableWidgetMenu
                />
                <Widget buttonFlag={false} disableWidgetMenu inheritHeight bodyClass={classes.sectionBody}>
                    <ConnectHero
                        onViewPricing={() => scrollToSection('pricing')}
                        onBrowseIntegrations={() => scrollToSection('integrations')}
                    />
                </Widget>
            </div>

            {/* Sticky tab bar — direct child of the column so it sticks across the whole scroll. */}
            <ConnectTabs active={active} onSelect={scrollToSection} />

            <div
                className={classnames(classes.widgetContainer, classes.sectionAnchor)}
                ref={aboutRef}
            >
                <Widget buttonFlag={false} disableWidgetMenu inheritHeight bodyClass={classes.sectionBody}>
                    <ConnectAboutSection />
                </Widget>
            </div>

            <div
                className={classnames(classes.widgetContainer, classes.sectionAnchor)}
                ref={integrationsRef}
            >
                <Widget buttonFlag={false} disableWidgetMenu inheritHeight bodyClass={classes.sectionBody}>
                    <ConnectIntegrationsSection />
                </Widget>
            </div>

            <div
                className={classnames(classes.widgetContainer, classes.sectionAnchor)}
                ref={pricingRef}
            >
                <Widget buttonFlag={false} disableWidgetMenu inheritHeight bodyClass={classes.sectionBody}>
                    <PricingSection
                        plans={plans}
                        loading={loading}
                        onChoosePlan={gatedHandleChoosePlan}
                        currency={currency}
                        ctaLabelPrefix="SUBSCRIBE TO"
                        header={PRICING_HEADER}
                        layout="responsive"
                    />
                </Widget>
            </div>

            <div
                className={classnames(classes.widgetContainer, classes.sectionAnchor)}
                ref={faqRef}
            >
                <Widget buttonFlag={false} disableWidgetMenu inheritHeight bodyClass={classes.sectionBody}>
                    <ConnectFaqSection />
                </Widget>
            </div>

            <CheckoutDrawer
                open={checkoutOpen}
                plan={selectedPlan}
                onClose={closeCheckout}
                onSuccess={({ email, planName }) => {
                    history.push('/admin/analytics/connect/welcome', { email, planName });
                }}
            />
            {renderUpgradeDialog()}
        </div>
    );
}

const ObservedPlansBrowseView = observer(PlansBrowseView);
export default ObservedPlansBrowseView;
